/**
 * 订阅相关可见部件的元数据（T14）。
 *
 * 与其它页面一致：每个「可以看到」的部件都在这里声明 id / 角色 / 中文名 /
 * 390x844 逻辑坐标 / zIndex / props / notes，组件通过 `buildComponentMeta()`
 * 取同一份数据登记进全局清单，安卓移植时直接复用坐标。
 *
 * 两组坐标：
 * - `page: 'settings'`：设置页里的「官方订阅」入口卡片，接在「数据备份 / 恢复」
 *   区块下方（设置页内容区可纵向滚动，坐标是含状态栏偏移的画布坐标）；
 * - `page: 'subscription'`：套餐选择页本体（覆盖整个可见内容区，390 x 744）。
 *
 * `SUBSCRIPTION_COMPONENTS` 会并入 `scripts/generate-manifest.ts` 的静态采集，
 * 保证「页面上登记的坐标」与「清单产物」一致。
 */

import { buildComponentMeta } from '../../ui';
import type { BoxLayout, ComponentMetaInput } from '../../ui';
import { PLAN_ORDER, PLANS } from './plan';
import type { PlanTier } from './plan';

type ComponentRole = NonNullable<ComponentMetaInput['role']>;

interface MetaOptions {
  zIndex?: number;
  props?: string[];
  notes?: string;
}

function meta(
  id: string,
  page: string,
  role: ComponentRole,
  label: string,
  layout: BoxLayout,
  options: MetaOptions = {},
): ComponentMetaInput {
  return {
    id,
    page,
    role,
    label,
    layout,
    zIndex: options.zIndex ?? 1,
    ...(options.props === undefined ? {} : { props: options.props }),
    ...(options.notes === undefined ? {} : { notes: options.notes }),
  };
}

/* ------------------------------------------------------------------ *
 * 设置页入口卡片（page: settings）
 * ------------------------------------------------------------------ */

export const SUBSCRIPTION_ENTRY_META = meta(
  'settings.subscription.entry',
  'settings',
  'button',
  '官方订阅',
  { x: 12, y: 984, width: 366, height: 88 },
  {
    zIndex: 3,
    props: ['selected'],
    notes:
      '设置页「官方订阅」入口卡片，位于「数据备份 / 恢复」下方（随设置页内容区滚动）；点击进入套餐选择页；文案提示「选择套餐 / 自行配置模型」两种模式互斥',
  },
);

export const SUBSCRIPTION_ENTRY_LABEL_META = meta(
  'settings.subscription.entry.label',
  'settings',
  'text',
  '官方订阅',
  { x: 24, y: 996, width: 160, height: 24 },
  { zIndex: 4, notes: '入口卡片主标题，安卓对应 TextView' },
);

export const SUBSCRIPTION_ENTRY_VALUE_META = meta(
  'settings.subscription.entry.value',
  'settings',
  'text',
  '当前订阅状态',
  { x: 180, y: 996, width: 186, height: 24 },
  { zIndex: 4, notes: '右侧状态文案：未选择 / 已选套餐 / 自行配置' },
);

export const SUBSCRIPTION_ENTRY_HINT_META = meta(
  'settings.subscription.entry.hint',
  'settings',
  'text',
  '订阅模式说明',
  { x: 24, y: 1024, width: 342, height: 36 },
  { zIndex: 4, notes: '说明「选择套餐」与「自行配置模型」两种模式互斥，二选一' },
);

/* ------------------------------------------------------------------ *
 * 套餐选择页（page: subscription）
 * ------------------------------------------------------------------ */

export const PLAN_PAGE_META = meta(
  'subscription.root',
  'subscription',
  'container',
  '套餐选择页容器',
  { x: 0, y: 24, width: 390, height: 744 },
  { zIndex: 50, notes: '覆盖可见内容区的整页容器；安卓对应 Fragment 根布局' },
);

export const PLAN_PAGE_BACKDROP_META = meta(
  'subscription.backdrop',
  'subscription',
  'background',
  '套餐选择页底色',
  { x: 0, y: 24, width: 390, height: 744 },
  { zIndex: 49, notes: '整页不透明底色，遮住下方设置页' },
);

export const PLAN_PAGE_BACK_META = meta(
  'subscription.header.back',
  'subscription',
  'button',
  '返回设置',
  { x: 8, y: 32, width: 44, height: 44 },
  { zIndex: 52, props: ['pressed'], notes: '返回按钮，安卓对应 Toolbar 的 navigationIcon' },
);

export const PLAN_PAGE_TITLE_META = meta(
  'subscription.header.title',
  'subscription',
  'text',
  '选择套餐',
  { x: 60, y: 40, width: 220, height: 28 },
  { zIndex: 52, notes: '页面标题，安卓对应 Toolbar title' },
);

export const PLAN_PAGE_SUBTITLE_META = meta(
  'subscription.header.subtitle',
  'subscription',
  'text',
  '套餐说明',
  { x: 16, y: 80, width: 358, height: 18 },
  { zIndex: 52, notes: '说明三档套餐按能力递增' },
);

/** 卡片起始 y 与卡片高度 / 间距。 */
export const CARD_TOP = 108;
export const CARD_HEIGHT = 140;
export const CARD_GAP = 12;
export const CARD_X = 16;
export const CARD_WIDTH = 358;

/** 第 index 档卡片的画布布局。 */
export function planCardLayout(index: number): BoxLayout {
  return {
    x: CARD_X,
    y: CARD_TOP + index * (CARD_HEIGHT + CARD_GAP),
    width: CARD_WIDTH,
    height: CARD_HEIGHT,
  };
}

export function planCardMetaId(tier: PlanTier, suffix: string): string {
  return `subscription.card.${tier}.${suffix}`;
}

interface CardMeta {
  card: ComponentMetaInput;
  name: ComponentMetaInput;
  price: ComponentMetaInput;
  tagline: ComponentMetaInput;
  capabilities: ComponentMetaInput;
  highlights: ComponentMetaInput;
  check: ComponentMetaInput;
}

function buildCardMeta(tier: PlanTier, index: number): CardMeta {
  const box = planCardLayout(index);
  const plan = PLANS[tier];
  return {
    card: meta(
      planCardMetaId(tier, 'root'),
      'subscription',
      'button',
      `${plan.name}卡片`,
      box,
      {
        zIndex: 53,
        props: ['selected'],
        notes: `整卡可点选；安卓对应 CardView + clickable。套餐档位：${tier}`,
      },
    ),
    name: meta(
      planCardMetaId(tier, 'name'),
      'subscription',
      'text',
      `${plan.name}名称`,
      { x: box.x + 12, y: box.y + 12, width: 180, height: 24 },
      { zIndex: 54, notes: '套餐名，安卓对应 TextView' },
    ),
    price: meta(
      planCardMetaId(tier, 'price'),
      'subscription',
      'text',
      `${plan.name}价格占位`,
      { x: box.x + 200, y: box.y + 12, width: 146, height: 24 },
      { zIndex: 54, notes: '价格占位（真实计费留后续任务），右对齐' },
    ),
    tagline: meta(
      planCardMetaId(tier, 'tagline'),
      'subscription',
      'text',
      `${plan.name}卖点`,
      { x: box.x + 12, y: box.y + 40, width: 334, height: 18 },
      { zIndex: 54, notes: '一句话卖点，次要文本色' },
    ),
    capabilities: meta(
      planCardMetaId(tier, 'capabilities'),
      'subscription',
      'text',
      `${plan.name}能力矩阵`,
      { x: box.x + 12, y: box.y + 68, width: 334, height: 20 },
      { zIndex: 54, notes: '能力徽标行：文字 / 图片 / 声 / 全自动语音' },
    ),
    highlights: meta(
      planCardMetaId(tier, 'highlights'),
      'subscription',
      'text',
      `${plan.name}能力要点`,
      { x: box.x + 12, y: box.y + 94, width: 300, height: 34 },
      { zIndex: 54, notes: '两行能力要点，安卓对应 TextView(maxLines=2)' },
    ),
    check: meta(
      planCardMetaId(tier, 'check'),
      'subscription',
      'icon',
      `${plan.name}选中标记`,
      { x: box.x + 318, y: box.y + 100, width: 28, height: 28 },
      { zIndex: 55, props: ['selected'], notes: '选中态对勾；安卓对应 CheckBox/Icon' },
    ),
  };
}

/** 三张档位卡片（按展示顺序）。 */
export const PLAN_CARD_METAS: Readonly<Record<PlanTier, CardMeta>> = {
  text: buildCardMeta('text', 0),
  multimodal: buildCardMeta('multimodal', 1),
  voice: buildCardMeta('voice', 2),
};

export const PLAN_CARD_META_LIST: readonly CardMeta[] = PLAN_ORDER.map(
  (tier) => PLAN_CARD_METAS[tier],
);

/** 「自行配置模型」选项。 */
export const BYO_CARD_META = meta(
  'subscription.byo.card',
  'subscription',
  'button',
  '不使用官方订阅',
  { x: 16, y: 564, width: 358, height: 92 },
  {
    zIndex: 53,
    props: ['selected'],
    notes: '自行配置文字 / 生图 / TTS 模型；与官方套餐互斥',
  },
);

export const BYO_TITLE_META = meta(
  'subscription.byo.title',
  'subscription',
  'text',
  '不使用官方订阅',
  { x: 28, y: 576, width: 300, height: 22 },
  { zIndex: 54, notes: '选项标题' },
);

export const BYO_HINT_META = meta(
  'subscription.byo.hint',
  'subscription',
  'text',
  '自行配置模型说明',
  { x: 28, y: 600, width: 334, height: 44 },
  { zIndex: 54, notes: '说明需自行配置文字 / 生图 / TTS 模型，且与官方套餐互斥' },
);

export const BYO_CHECK_META = meta(
  'subscription.byo.check',
  'subscription',
  'icon',
  '自行配置选中标记',
  { x: 318, y: 588, width: 28, height: 28 },
  { zIndex: 55, props: ['selected'], notes: '选中态对勾' },
);

/** 底部当前状态与互斥提示。 */
export const PLAN_STATUS_META = meta(
  'subscription.status',
  'subscription',
  'text',
  '当前订阅状态',
  { x: 16, y: 672, width: 358, height: 22 },
  { zIndex: 52, notes: '显示当前选择；未选择时给引导' },
);

export const PLAN_STATUS_HINT_META = meta(
  'subscription.status.hint',
  'subscription',
  'text',
  '订阅模式互斥说明',
  { x: 16, y: 698, width: 358, height: 40 },
  { zIndex: 52, notes: '「官方套餐」与「自行配置」互斥，二选一' },
);

/**
 * 订阅功能声明的全部部件。
 *
 * 并入 `scripts/generate-manifest.ts` 的静态采集 —— 选择页不在示例目录
 * （`ManifestCatalog`）里渲染，必须靠这份声明进入清单产物。
 */
export const SUBSCRIPTION_COMPONENTS: readonly ComponentMetaInput[] = [
  SUBSCRIPTION_ENTRY_META,
  SUBSCRIPTION_ENTRY_LABEL_META,
  SUBSCRIPTION_ENTRY_VALUE_META,
  SUBSCRIPTION_ENTRY_HINT_META,
  PLAN_PAGE_META,
  PLAN_PAGE_BACKDROP_META,
  PLAN_PAGE_BACK_META,
  PLAN_PAGE_TITLE_META,
  PLAN_PAGE_SUBTITLE_META,
  ...PLAN_CARD_META_LIST.flatMap((card) => [
    card.card,
    card.name,
    card.price,
    card.tagline,
    card.capabilities,
    card.highlights,
    card.check,
  ]),
  BYO_CARD_META,
  BYO_TITLE_META,
  BYO_HINT_META,
  BYO_CHECK_META,
  PLAN_STATUS_META,
  PLAN_STATUS_HINT_META,
];

/** 取完整 ComponentMeta（登记进注册表用）。 */
export function subscriptionComponentMeta(input: ComponentMetaInput): ReturnType<
  typeof buildComponentMeta
> {
  return buildComponentMeta(input);
}